import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';

import type { OrgUnitData } from './constants';

type UseExportOptionsProps = {
  rootOrgUnit: OrgUnitData | undefined;
  childOrgUnits: (OrgUnitData | undefined)[];
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

export const useExportOptions = ({
  rootOrgUnit,
  childOrgUnits,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const { t } = useTranslation('followUp');

  return useMemo(() => {
    const rows: Row[] = [
      [
        { value: t('statistics.orgUnit'), fontWeight: 'bold' },
        { value: t('statistics.invitees'), fontWeight: 'bold' },
        { value: t('statistics.respondents'), fontWeight: 'bold' },
        { value: t('statistics.responseRate'), fontWeight: 'bold' },
      ],
      ...[rootOrgUnit, ...childOrgUnits].map(
        (orgUnit) =>
          [
            {
              value: orgUnit?.label ?? '',
            },
            {
              value: orgUnit?.invitees ?? '',
            },
            {
              value: orgUnit?.respondents ?? '',
            },
            {
              value: `${orgUnit?.respondentPercentage ?? ''}%`,
              align: 'right',
            },
          ] satisfies Row,
      ),
    ];

    return {
      widgetName: widgetName,
      rows,
      queryFilters,
    };
  }, [t, rootOrgUnit, childOrgUnits, widgetName, queryFilters]);
};
